import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectAccountInfo } from '@features/settings/store/payments.duck';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { PaymentFrequency } from '@features/auth/services/AccountService';
import { useTranslation } from 'react-i18next';

interface Props {
  // Number of licences selected (1 licence === stripePackSize users).
  licences: number;
  // The total amount the user pays (minor currency units).
  billTotal: number;
}

// Itemised cost breakdown for the selected number of licences. unitAmount is the price per licence
// pack; billTotal is the total the user pays.
export function PriceBreakdown({ licences, billTotal }: Props) {
  const accountInfo = useSelector(selectAccountInfo);
  const { t } = useTranslation();

  if (accountInfo?.unitAmount == null) {
    return null;
  }

  const currency = (accountInfo.currency || 'GBP').toUpperCase();
  const format = (minorUnits: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minorUnits / 100);

  const perLicence = accountInfo.unitAmount;
  const total = billTotal;
  const period = t(accountInfo.paymentFrequency === PaymentFrequency.Yearly ? 'payments.period-year' : 'payments.period-month');

  return (
    <Breakdown>
      <Row>
        <BodySmall>{t('payments.price-per-licence')}</BodySmall>
        <BodySmall>{`${format(perLicence)} / ${period}`}</BodySmall>
      </Row>
      <Row>
        <BodySmall>{t('payments.licences')}</BodySmall>
        <BodySmall>{`× ${licences} (${licences / 10} packs of 10)`}</BodySmall>
      </Row>
      <Divider />
      <Row>
        <BodySmall weight={600}>{t('payments.total')}</BodySmall>
        <BodySmall weight={600}>{`${format(total)} / ${period}`}</BodySmall>
      </Row>
    </Breakdown>
  );
}

const Breakdown = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  p {
    margin: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px solid ${Colours.mildGrey};
`;
